import { ApplicationDependencyService, ApplicationGroupingService,
    ApplicationServerService, ApplicationService, DatabaseApplicationService, ServerCommunicationService,
} from '@amzn/awscat-portfolio-assessment-service-client';
import { AppDependency, ApplicationGroup, ApplicationToServer, DatabaseApplication, DeserializedApplication,
    GroupsStatusUpdate,
    ServerCommunication } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Box, ButtonDropdown, ButtonDropdownProps, NonCancelableCustomEvent, SpaceBetween } from '@amzn/awsui-components-react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { formatNumberAsString, formatPercentage, formatPercentageAsString } from '../../../common/LocalizationHelper';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes } from '../../../common/table/types';
import { ErrorWithResponse, MatchParam } from '../../../common/types';
import { ONE_HUNDRED } from '../../../common/uomConversion';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { getCell } from '../../common/awsui-table-helper';
import ConfirmationModal from '../../common/ConfirmationModal';
import ApplicationGroupCompositionGraph from './ApplicationGroupCompositionGraph';
import { EnhancedApplication, GROUPING_ACTIONS, STATUS_IN_GROUPING } from './constants';
import InReviewDependencyModal from './InReviewDependencyModal';
import RemoveApplicationModal from './RemoveApplicationModal';
import RemoveDependencyByType from './RemoveByType';
import SharedInfrastructureModal from './SharedInfrastructureModal';

const applicationService = new ApplicationService();
const applicationGroupingService = new ApplicationGroupingService();
const applicationDependencyService = new ApplicationDependencyService();
const applicationServerService = new ApplicationServerService();
const databaseApplicationService = new DatabaseApplicationService();
const serverCommunicationService = new ServerCommunicationService();

type Props = RouteComponentProps<MatchParam> & FlashContextInterface;

function ApplicationGrouping({match, flash}: Props) {
    const { params } = match;
    const { portfolioId } = params;

    const [ loading, setLoading ] = useState(false);
    const [ applicationMap, setApplicationMap ] = useState<Map<string, EnhancedApplication>>(new Map());
    const [ applicationGroups, setApplicationGroups ] = useState<ApplicationGroup[]>([]);
    const [ selectedGroups, setSelectedGroups ] = useState<ApplicationGroup[]>([]);
    const [ applicationDependencies, setApplicationDependencies ] = useState<AppDependency[]>([]);
    const [ , setServerCommunications ] = useState<ServerCommunication[]>([]);
    const [ appServerMappings, setAppServerMappings ] = useState<ApplicationToServer[]>([]);
    const [ appDatabaseMappings, setAppDatabaseMappings ] = useState<DatabaseApplication[]>([]);
    const [ isAnyGroupInWave, setIsAnyGroupInWave ] = useState(false);
    const [ inferWarningVisible, setInferWarningVisible ] = useState(false);
    const [ removeByTypeVisible, setRemoveByTypeVisible ] = useState(false);
    const [ removeApplicationVisible, setRemoveApplicationVisible ] = useState(false);
    const [ markAsSharedInfraVisible, setMarkAsSharedInfraVisible ] = useState(false);
    const [ inReviewDependenciesVisible, setInReviewDependenciesVisible ] = useState(false);
    const [ recreateGroupWarningVisible, setRecreateGroupWarningVisible ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);
    const history = useHistory();

    const isMounted = useIsMounted();

    const loadApplications = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const applications: DeserializedApplication[] = await applicationService.list(portfolioId, true);
                applicationMap.clear();
                applications.forEach(application => applicationMap.set(application.applicationId, { ...application, groupName: ''}));
                setApplicationMap(applicationMap);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.APPLICATION_ERROR_LOADING);
        }
    };

    const loadApplicationGroups = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const groups: ApplicationGroup[] = await applicationGroupingService.list(portfolioId);
                setApplicationGroups(groups);
                setIsAnyGroupInWave(groups.some(group => group.migrationWaveId));

                groups.forEach(group => group.applicationIds.forEach(appId => {
                    const app = applicationMap.get(appId);
                    if (app) {
                        app.groupName = group.name;
                        applicationMap.set(appId, app);
                    }
                }));
                setApplicationMap(applicationMap);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_GROUPS);
        }
    };

    const loadServerCommunications = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const serverComm = await serverCommunicationService.list(portfolioId);
                setServerCommunications(serverComm);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_SERVER_COMMUNICATION);
        }
    };

    const loadApplicationServerMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appServer = await applicationServerService.list(portfolioId);
                setAppServerMappings(appServer);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_SERVER_MAPPING);
        }
    };

    const loadApplicationDatabaseMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appDatabase = await databaseApplicationService.list(portfolioId);
                setAppDatabaseMappings(appDatabase);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_DATABASE_MAPPING);
        }
    };

    const loadApplicationDependenciesMappings = async (): Promise<void> => {
        try {
            if (portfolioId) {
                const appDependencies = (await applicationDependencyService.list(portfolioId)).filter(dependency => dependency.inScope);
                setApplicationDependencies(appDependencies);
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_APPLICATION_DEPENDENCIES_MAPPING);
        }
    };

    const loadData = async (): Promise<void> => {
        setLoading(true);
        await loadApplications();
        await loadApplicationGroups();
        await loadServerCommunications();
        await loadApplicationServerMappings();
        await loadApplicationDatabaseMappings();
        await loadApplicationDependenciesMappings();
        if (isMounted()) {
            setLoading(false);
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.APPLICATION_GROUPS);
    }, [ panelHandler ]);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [portfolioId]);

    const columnDefinition: ColumnDefinition<ApplicationGroup>[] = useMemo(() => [
        {
            id: 'groupName',
            header: constants.GROUP_NAME,
            data: item => item.name,
            cell: item => <a
                className='clickable'
                href='#'
                onClick={() => history.push(`/portfolio/${portfolioId}/planning/grouping/${item.applicationGroupId}`)}
            >
                {item.name}
            </a>,
            type: ColumnTypes.TEXT,
        },
        {
            id: 'status',
            header: constants.STATUS,
            data: e => e.locked ? constants.CLOSE : constants.OPEN,
            cell: e => e.locked ? constants.CLOSE : constants.OPEN,
            type: ColumnTypes.TEXT,
        },
        {
            id: 'totalApplications',
            header: constants.NUMBER_OF_APPLICATIONS,
            cell: item => getCell(formatNumberAsString(item.totalApplications)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'totalServers',
            header: constants.NUMBER_OF_SERVERS,
            cell: item => getCell(formatNumberAsString(item.totalServers)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'totalDatabases',
            header: constants.NUMBER_OF_DATABASES,
            cell: item => getCell(formatNumberAsString(item.totalDatabases)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'avgDataCollectedPct',
            header: constants.DATA_VALIDATED,
            // Workaround for Pct issue: https://sim.amazon.com/issues/PAT-31622
            cell: item => getCell(formatPercentage(item.avgDataCollectedPct / ONE_HUNDRED)), 
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'avgApplicationDataCollectedPct',
            header: constants.APPLICATION_DATA_VALIDATED,
            data: item => formatPercentageAsString(item.avgApplicationDataCollectedPct / ONE_HUNDRED),
            // Workaround for Pct issue: https://sim.amazon.com/issues/PAT-31622
            cell: item => getCell(formatPercentage(item.avgApplicationDataCollectedPct / ONE_HUNDRED)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'avgServerDataCollectedPct',
            header: constants.SERVER_DATA_VALIDATED,
            data: item => formatPercentageAsString(item.avgServerDataCollectedPct / ONE_HUNDRED),
            // Workaround for Pct issue: https://sim.amazon.com/issues/PAT-31622
            cell: item => getCell(formatPercentage(item.avgServerDataCollectedPct / ONE_HUNDRED)),
            type: ColumnTypes.NUMBER,
        },
        {
            id: 'avgDatabaseDataCollectedPct',
            header: constants.DATABASE_DATA_VALIDATED,
            data: item => formatPercentageAsString(item.avgDatabaseDataCollectedPct / ONE_HUNDRED),
            // Workaround for Pct issue: https://sim.amazon.com/issues/PAT-31622
            cell: item => getCell(formatPercentage(item.avgDatabaseDataCollectedPct / ONE_HUNDRED)),
            type: ColumnTypes.NUMBER,
        },
    ], [history, portfolioId]);

    const statusUpdateOptions: ButtonDropdownProps.Items = [
        {
            id: constants.OPEN, 
            text: constants.OPEN,
        },
        {
            id: constants.CLOSE, 
            text: constants.CLOSE,
        },
    ];

    const onStatusUpdate = async ({ detail }: NonCancelableCustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        setLoading(true);
        try {
            const updateDetails: GroupsStatusUpdate = {
                portfolioId,
                applicationGroupIds: selectedGroups.map(group => group.applicationGroupId),
                locked: detail.id === constants.CLOSE,
            };
            await applicationGroupingService.updateStatus(updateDetails);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_UPDATING_GROUP_STATUS);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
        loadData();
    };

    const onTableSelectionChange = (items: readonly ApplicationGroup[] | undefined): void => {
        const groupsSelection = items ? [...items] : [];
        setSelectedGroups(groupsSelection);
    };

    const applicationGroupTable = (
        <AWSCATTable
            resourceName={constants.APPLICATION_GROUPS_TABLE_RESOURCE_NAME}
            items={applicationGroups}
            columnDefinitions={columnDefinition}
            loading={loading}
            autoGenerateVisibleContent={true}
            selectionType='multi'
            onSelectionChange={onTableSelectionChange}
            useDefaultDownload
            headerActions={
                <ButtonDropdown
                    items={statusUpdateOptions}
                    variant='normal'
                    onItemClick={onStatusUpdate}
                    disabled={!(selectedGroups?.length)}
                >
                    {constants.UPDATE_STATUS_DROPDOWN_LABEL}
                </ButtonDropdown>
            }
        />
    );

    const recreateGroups = async () => {
        setLoading(true);
        try {
            await applicationGroupingService.createGroups(portfolioId);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_RECREATING_GROUPS);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
                setRecreateGroupWarningVisible(false);
            }
        }
        loadData();
    };

    const inferGroups = async () => {
        setLoading(true);
        try {
            // TODO: these two bulk-update operations should be built into the infer API instead
            // https://issues.amazon.com/issues/V490810173
            await databaseApplicationService.update({portfolioId, fieldName: 'statusInGrouping', fieldValue: STATUS_IN_GROUPING.ACTIVE});
            await applicationServerService.update({portfolioId, fieldName: 'statusInGrouping', fieldValue: STATUS_IN_GROUPING.ACTIVE});
            await applicationDependencyService.infer(portfolioId);
            await applicationGroupingService.createGroups(portfolioId);
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as ErrorWithResponse).response?.data?.message ?? (err as Error).message, constants.CANNOT_INFER_DEPENDENCIES);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
                setInferWarningVisible(false);
            }
        }
        loadData();
    };

    const onActionClicked = (event: CustomEvent<ButtonDropdownProps.ItemClickDetails>) => {
        switch (event.detail.id) {
            case 'inferDependencies':
                if (applicationDependencies.length || isAnyGroupInWave) {
                    setInferWarningVisible(true);
                } else {
                    inferGroups();
                }
                break;
            case 'removeByType':
                setRemoveByTypeVisible(true);
                break;
            case 'removeApplications':
                setRemoveApplicationVisible(true);
                break;
            case 'markAsSharedInfra':
                setMarkAsSharedInfraVisible(true);
                break;
            case 'inReview':
                setInReviewDependenciesVisible(true);
                break;
            case 'recreateGroups':
                if (isAnyGroupInWave) {
                    setRecreateGroupWarningVisible(true);
                } else {
                    recreateGroups();
                }
                break;
        }
    };

    return (
        <div>
            <SpaceBetween size='l'>
                <Box float='right'>
                    <ButtonDropdown
                        disabled={loading}
                        onItemClick={onActionClicked}
                        items={GROUPING_ACTIONS}>
                        {constants.ACTION_BUTTON_TEXT}
                    </ButtonDropdown>
                </Box>
                <ApplicationGroupCompositionGraph inferGroups={inferGroups} loading={loading} applicationGroups={applicationGroups}/>
                {applicationGroupTable}
                {<ConfirmationModal
                    header={constants.START_FROM_THE_BEGINNING}
                    visible={inferWarningVisible}
                    confirmButton={constants.PROCEED}
                    confirmLoading={loading}
                    onConfirm={inferGroups}
                    onDismiss={() => { setInferWarningVisible(false); }}
                    warning={isAnyGroupInWave ? constants.START_FROM_THE_BEGINNING_WARNING_WAVES : ''}
                >
                    {constants.START_FROM_THE_BEGINNING_WARNING}
                </ConfirmationModal>}
                {removeByTypeVisible ? <RemoveDependencyByType onDismiss={() => setRemoveByTypeVisible(false)}  loadData={loadData}/> : null}
                {removeApplicationVisible ? (
                    <RemoveApplicationModal
                        onDismiss={() => setRemoveApplicationVisible(false)}
                        loadData={loadData}
                        groups={applicationGroups}
                        dependencies={applicationDependencies}
                        appServerMappings={appServerMappings}
                        appDatabaseMappings={appDatabaseMappings}
                    />
                )
                    : null}
                {markAsSharedInfraVisible ? (
                    <SharedInfrastructureModal
                        onDismiss={() => setMarkAsSharedInfraVisible(false)}
                        onConfirm={() => {
                            setMarkAsSharedInfraVisible(false);
                            loadData();
                        }}
                        groups={applicationGroups}
                        applicationMap={applicationMap}
                        appServerMappings={appServerMappings}
                        appDatabaseMappings={appDatabaseMappings}

                    />
                )
                    : null}
                {inReviewDependenciesVisible ? (
                    <InReviewDependencyModal
                        onDismiss={() => setInReviewDependenciesVisible(false)}
                        onConfirm={() => {
                            setInReviewDependenciesVisible(false);
                            loadData();
                        }}
                        appServerMappings={appServerMappings}
                        appDatabaseMappings={appDatabaseMappings}
                    />
                )
                    : null}
                {recreateGroupWarningVisible ? (
                    <ConfirmationModal
                        header={constants.WARNING_HEADER}
                        visible={inferWarningVisible}
                        confirmButton={constants.PROCEED}
                        confirmLoading={loading}
                        onConfirm={recreateGroups}
                        onDismiss={() => { setRecreateGroupWarningVisible(false); }}
                    >
                        {constants.RECREATE_GROUPS_WARNING}
                    </ConfirmationModal>
                )
                    : null}
            </SpaceBetween>
        </div>
    );
}

export default withFlashContext(ApplicationGrouping);
